import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
    compName_character,
    livePortsNumber,
} from '../logic/panels/helpers/LiveButton'
import { parseLiveBytes } from './liveBytesParser'

/**
 * LiveTackButtonControl – Momentary (press-and-hold) button for PlayScreen.
 *
 * Held DOWN → ON (sends savedBytes to hardware).
 * Released / pointer leaves → OFF (sends 0 to hardware).
 * Uses the same Live protocol (LS / LW) as MotorSlider.
 *
 * Props:
 *   comp           – full component object from play data
 *   outputStates   – current output state map
 *   onChange        – (mapping, value) callback
 *   resolveImage   – image resolver function
 *   highlightClass – CSS class string for step highlighting
 *   style          – positioning style object
 */

const DUAL_PORT_MOTORS = [
    'mini_geared_motor',
    'geared_motor',
    'dc_motor',
    'dynamex_motor',
]

function LiveTackButtonControl({
    comp,
    outputStates,
    onChange,
    resolveImage,
    highlightClass,
    style,
    isLiveStep,
    onRequestActivate,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)
    const isPressedRef = useRef(false)
    const cleanupRefs = useRef({})

    const componentType = (comp.name || '').toLowerCase()
    const isDualPort = DUAL_PORT_MOTORS.includes(componentType)
    const port = comp.port

    // free=true → use fixedBytes (recorded); free=false → use direct (raw paste)
    const free = comp.properties.free ?? true
    const fixedBytes = comp.properties.fixedBytes || null
    const directBytes =
        comp.properties.direct && comp.properties.direct.length > 0
            ? comp.properties.direct
            : null
    const rawBytes = free ? null : directBytes
    const savedBytes = free ? fixedBytes : null

    // Live Bytes: admin-entered alphanumeric string (e.g. "W7" / "WMT2").
    // Letters → ASCII byte code; each run of digits → a single numeric byte
    // (e.g. "W13" → [87, 13], "WMT2" → [87, 77, 84, 2]). Sent once per press.
    const liveBytesEnabled = comp.properties.liveBytesEnabled ?? false
    const stopAction = comp.properties.stopAction ?? false
    const liveBytesStr = comp.properties.liveBytes || ''
    const liveBytesArray = useMemo(
        () => parseLiveBytes(liveBytesStr),
        [liveBytesStr]
    )

    const { port1, port2 } = useMemo(() => {
        if (!isDualPort || !port) return { port1: port, port2: null }
        const match = port.match(/^([A-Za-z]+)(\d+)$/)
        if (match) {
            const letter = match[1]
            const num = parseInt(match[2])
            return { port1: port, port2: `${letter}${num + 1}` }
        }
        return { port1: port + '1', port2: port + '2' }
    }, [port, isDualPort])

    const liveChange = useCallback(
        (targetPort, value) => {
            if (!worker || !webSerial?.isConnected) return
            const connectedDevice = sessionStorage.getItem('connectedDevice')
            if (!connectedDevice) return

            let adjustedValue = value
            if (componentType === 'servo_motor_270') {
                adjustedValue = Math.round(0.6667 * value)
            } else if (componentType === 'servo_motor_360') {
                adjustedValue = 100 + value
            }

            const charCode =
                compName_character[componentType] || 'P'.charCodeAt(0)
            const portNum =
                livePortsNumber?.[connectedDevice]?.[targetPort] ||
                livePortsNumber?.Ace?.[targetPort]
            if (portNum === undefined) return

            const data = [
                'L'.charCodeAt(0),
                'W'.charCodeAt(0),
                charCode,
                portNum,
                adjustedValue,
            ]
            worker.postMessage({ type: 'writeArray', value: data })
        },
        [componentType, worker, webSerial]
    )

    const sendLiveValue = useCallback(
        async (value) => {
            if (!port) return

            if (isDualPort) {
                if (value < 0) {
                    const absVal =
                        componentType === 'dynamex_motor'
                            ? Math.round(Math.abs(value) * 0.8)
                            : Math.abs(value)
                    liveChange(port1, absVal)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port2, 0)
                } else if (value > 0) {
                    const v =
                        componentType === 'dynamex_motor'
                            ? Math.round(value * 0.8)
                            : value
                    liveChange(port2, v)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port1, 0)
                } else {
                    liveChange(port2, 0)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port1, 0)
                }
            } else {
                liveChange(port, value)
            }
        },
        [port, isDualPort, port1, port2, liveChange, componentType]
    )

    const sendRawBytes = useCallback(async () => {
        if (!rawBytes || !worker || !webSerial?.isConnected) return
        worker.postMessage({ type: 'writeArray', value: rawBytes })
    }, [rawBytes, worker, webSerial])

    const sendSavedBytes = useCallback(async () => {
        if (!savedBytes || !worker || !webSerial?.isConnected) return
        for (const bytes of savedBytes) {
            worker.postMessage({ type: 'writeArray', value: bytes })
            await new Promise((r) => setTimeout(r, 100))
        }
    }, [savedBytes, worker, webSerial])

    const sendOffBytes = useCallback(async () => {
        if (!port || !worker || !webSerial?.isConnected) return
        await sendLiveValue(0)
    }, [port, worker, webSerial, sendLiveValue])

    const sendLiveBytes = useCallback(() => {
        if (!liveBytesEnabled || liveBytesArray.length === 0) return
        if (!worker || !webSerial?.isConnected) return
        worker.postMessage({ type: 'writeArray', value: liveBytesArray })
        console.log('Sending live bytes:', liveBytesArray)
    }, [liveBytesEnabled, liveBytesArray, worker, webSerial])

    // Keep cleanup refs current so the unmount safety net always calls the
    // latest versions of these functions without stale closure issues.
    cleanupRefs.current.onChange = onChange
    cleanupRefs.current.sendOffBytes = sendOffBytes
    cleanupRefs.current.mapping = comp.mapping

    // Unmount safety net: if the component unmounts while the button is
    // physically held (e.g. key-prop change triggers remount on step advance),
    // force the global state and hardware back to OFF.
    useEffect(() => {
        return () => {
            if (isPressedRef.current) {
                cleanupRefs.current.onChange(cleanupRefs.current.mapping, 0)
                cleanupRefs.current.sendOffBytes()
            }
        }
    }, [])

    // ── Momentary handlers ────────────────────────────────────────
    // Press down → ON
    const handlePointerDown = useCallback(
        async (e) => {
            // Respect the shared momentary-button cooldown; if blocked, the parent
            // shows a "please wait" notice and we do nothing.
            if (onRequestActivate && !onRequestActivate()) return
            e.currentTarget.setPointerCapture(e.pointerId)
            isPressedRef.current = true
            onChange(comp.mapping, 1)
            // Live Bytes fire once on every press, independent of the live-step
            // byte pipeline.
            sendLiveBytes()
            if (isLiveStep) return
            if (rawBytes) {
                await sendRawBytes()
            } else {
                await sendSavedBytes()
            }
        },
        [
            comp.mapping,
            onChange,
            isLiveStep,
            rawBytes,
            sendRawBytes,
            sendSavedBytes,
            sendLiveBytes,
            onRequestActivate,
        ]
    )

    // Release / leave / cancel.
    // NOTE: we intentionally do NOT switch the button OFF here. The button stays
    // in its ON state to show that the action is running; the parent (PlayScreen)
    // switches it back OFF once the board signals the action is complete (Rdone).
    const handlePointerUp = useCallback(
        async (e) => {
            isPressedRef.current = false
            if (stopAction) onChange(comp.mapping, 0)
            if (isLiveStep) return
            await sendOffBytes()
        },
        [isLiveStep, sendOffBytes, stopAction, onChange, comp.mapping]
    )

    // The button is "ON" visually if its value is non-zero
    const isBtnOn = (outputStates[comp.mapping] || 0) !== 0
    const btnImg = isBtnOn
        ? comp.properties.onImage || comp.properties.imageUrl
        : comp.properties.offImage || comp.properties.imageUrl

    return (
        <div
            key={comp.id}
            id={comp.id}
            className={`absolute-component playscreen-action-button${highlightClass}`}
            style={{ ...style, zIndex: style?.zIndex ?? 20 }}
        >
            <img
                className={comp.properties.className || ''}
                src={resolveImage(btnImg)}
                alt={comp.name}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onDragStart={(e) => e.preventDefault()}
                style={{ cursor: 'pointer', touchAction: 'none' }}
            />
            <span className="control-label">
                {comp.properties.label || comp.name}
            </span>
        </div>
    )
}

export default LiveTackButtonControl
